/**
 * PokerVault hover-magnifier.
 *
 * Adds a cursor-following lens over product-page gallery images on devices
 * that support real hover. The main image stays in place; the lens is centred
 * on the cursor and shows a zoomed crop of the area under it, using the
 * 3840px-wide URL that snippets/product-media.liquid already exposes via
 * data-max-resolution. The native cursor is hidden while the lens is up, so
 * the lens itself reads as the pointer.
 *
 * Skipped: inside the fullscreen zoom-dialog, on touch/coarse pointers, and
 * on non-image slides (we only bind to .product-media-container--image's <img>).
 */
(() => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

  const LENS_W = 220;
  const LENS_H = 130;
  const ZOOM = 1.5;
  const BOUND_FLAG = 'pvMagnifierBound';
  const LENS_CLASS = 'pv-hover-magnifier';
  const CURSOR_HIDDEN_CLASS = 'pv-magnifier-cursor-hidden';

  const resolveSource = (img) =>
    img?.dataset.maxResolution || img?.currentSrc || img?.src || '';

  const bind = (container) => {
    if (container.dataset[BOUND_FLAG]) return;
    if (container.closest('.dialog-zoomed-gallery')) return;
    // Defensive: card-gallery slides re-use the same .product-media-container--image
    // class. They have their own magnifier component
    // (assets/pokervault-card-thumbnail-magnifier.js) and the main scan
    // selector already excludes them, but bail here too in case a future
    // caller passes one in directly.
    if (container.closest('.card-gallery')) return;
    const img = container.querySelector('img.product-media__image');
    if (!img) return;
    container.dataset[BOUND_FLAG] = '1';

    // Host the lens inside slideshow-container rather than on <body>. The
    // gallery is its own stacking context -- slideshow-container's
    // container-type: inline-size applies layout containment -- so a
    // body-level lens paints above the whole subtree at any z-index, burying
    // the prev/next arrows. Sharing their context lets the arrows sit above
    // it. Unlike slideshow-slides and the slides themselves,
    // slideshow-container sets no overflow, so the lens still overhangs the
    // gallery edges. Grid presentation has no slideshow (and no arrows), so
    // fall back to the media container, which is likewise position:relative.
    const host = container.closest('slideshow-container') ?? container;

    let lens = null;

    /** @type {HTMLDialogElement | null} */
    let zoomDialog = null;
    // Resolved once and re-resolved only if the cached node leaves the DOM
    // (variant switches re-render the gallery), so the hot path is a property
    // read rather than a tree walk on every pointermove.
    const fullscreenOpen = () => {
      if (!zoomDialog?.isConnected) {
        zoomDialog = container.closest('media-gallery')?.querySelector('zoom-dialog dialog') ?? null;
      }
      return !!zoomDialog?.open;
    };

    const teardown = () => {
      if (lens) {
        lens.remove();
        lens = null;
      }
      img.classList.remove(CURSOR_HIDDEN_CLASS);
      window.removeEventListener('scroll', teardown, { capture: true });
    };

    const ensureLens = () => {
      if (lens) return lens;
      const src = resolveSource(img);
      if (!src) return null;
      // Self-heal: a gallery re-render mid-hover can remove the <img> without
      // firing pointerleave, stranding its lens on the host. Only one lens can
      // ever be wanted at a time -- there is one pointer.
      document.querySelectorAll(`.${LENS_CLASS}`).forEach((stray) => stray.remove());
      lens = document.createElement('div');
      lens.className = LENS_CLASS;
      lens.setAttribute('aria-hidden', 'true');
      lens.style.backgroundImage = `url("${src}")`;
      host.appendChild(lens);
      img.classList.add(CURSOR_HIDDEN_CLASS);
      // Bound to the lens's lifetime rather than to bind(), so a gallery of N
      // images doesn't leave N permanent scroll listeners on window.
      window.addEventListener('scroll', teardown, { passive: true, capture: true });
      requestAnimationFrame(() => {
        if (lens) lens.style.opacity = '1';
      });
      return lens;
    };

    const onMove = (event) => {
      if (fullscreenOpen()) {
        teardown();
        return;
      }
      if (!img.complete || img.naturalWidth === 0) return;

      const imgRect = img.getBoundingClientRect();
      const x = event.clientX - imgRect.left;
      const y = event.clientY - imgRect.top;
      if (x < 0 || y < 0 || x > imgRect.width || y > imgRect.height) return;

      const l = ensureLens();
      if (!l) return;

      // Centre the lens on the cursor, in host-relative coords. Deliberately
      // unclamped: nudging the lens back inside the gallery would move the
      // cursor off its centre, and with the cursor hidden there would be
      // nothing left showing where the pointer actually is. Backdrop-filter on
      // the lens shows blurred page content underneath wherever the magnified
      // image isn't painted (near the image's own edges).
      const hostRect = host.getBoundingClientRect();
      const lensX = event.clientX - hostRect.left - LENS_W / 2;
      const lensY = event.clientY - hostRect.top - LENS_H / 2;
      l.style.transform = `translate3d(${lensX}px, ${lensY}px, 0)`;

      // The lens centre is the cursor, so the pixel under the pointer is the
      // one magnified at the middle of the lens.
      l.style.backgroundSize = `${imgRect.width * ZOOM}px ${imgRect.height * ZOOM}px`;
      l.style.backgroundPosition = `${-(x * ZOOM - LENS_W / 2)}px ${-(y * ZOOM - LENS_H / 2)}px`;
    };

    // Listen on the <img> so we never see coordinates outside its rect, and
    // pointerleave on the img reliably fires when the cursor exits the visible
    // image bounds (avoids letterbox edge cases when aspect_ratio = adapt).
    img.addEventListener('pointerenter', onMove);
    img.addEventListener('pointermove', onMove);
    img.addEventListener('pointerleave', teardown);
    img.addEventListener('pointercancel', teardown);
  };

  const scan = (root) => {
    const scope = root && root.querySelectorAll ? root : document;
    // Scope to the main product gallery only. Card thumbnails re-use the same
    // inner class and are handled by pokervault-card-thumbnail-magnifier.js.
    scope
      .querySelectorAll('media-gallery .product-media-container--image')
      .forEach((el) => bind(el));
  };

  const start = () => {
    scan(document);

    const mo = new MutationObserver((mutations) => {
      for (const m of mutations) {
        m.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;
          if (
            node.matches?.('.product-media-container--image') &&
            node.closest('media-gallery') &&
            !node.closest('.card-gallery')
          ) {
            bind(node);
          }
          if (node.querySelectorAll) scan(node);
        });
      }
    });
    mo.observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
